import React, { useCallback, useContext, useRef, useMemo } from 'react'
import useClickOutside from '../../lib/hooks/useClickOutSide'
import List, { ListProps } from '../List'
import Popover from '../Popover'
import { MenuContext } from './Menu'
import MenuItem, { MenuDivider } from './MenuItem'

export interface MenuListProps extends ListProps<'ul'> {}

export const MenuList = ({ children, ...rest }: MenuListProps) => {
  const {
    state: { buttonRef, focusedIndex = -1, menuId, open = false, selectedIndex = -1 },
    dispatch,
  } = useContext(MenuContext)
  const paperRef = useRef<HTMLDivElement>(null)
  const listRef = useRef<HTMLUListElement>(null)
  const handleClickOutside = useCallback(() => {
    if (open) dispatch({ type: 'closeOutside' })
  }, [dispatch, open])
  useClickOutside(paperRef!, handleClickOutside)

  const menuItemsCount = useMemo<number>((): number => {
    return React.Children.toArray(children).filter((child: any): boolean => {
      // FIXME do that checking with a propType function instead ?
      // @see https://stackoverflow.com/a/30657554
      if (process.env.NODE_ENV !== 'production') {
        if (child.type !== MenuItem && child.type !== MenuDivider) {
          console.warn('[AIRBUS-DS] The MenuList component only accept ‘MenuItem‘ or ‘MenuDivider‘ as children.')
        }
      }
      return child.type === MenuItem
    }).length
  }, [children])

  const disabledIndexes = useMemo<number[]>((): number[] => {
    const result: number[] = []
    let menuItemIndex = -1
    React.Children.forEach(children, (child: any): void => {
      if (!child || child.type !== MenuItem) return
      menuItemIndex++
      if (child.props.disabled) result.push(menuItemIndex)
    })
    return result
  }, [children])

  // automatically set 'selected' prop on MenuItem children depending on selectedIndex state
  const renderChildren = () => {
    const childrenArray = React.Children.toArray(children)
    let menuItemIndex = -1
    return childrenArray.map((child: any) => {
      if (child.type !== MenuItem) return child
      menuItemIndex++
      return React.cloneElement(child, {
        focused: focusedIndex === menuItemIndex,
        selected: selectedIndex === menuItemIndex,
      })
    })
  }

  const handleKeyDown = (e: any) => {
    e.preventDefault() // prevent scroll
    const nextEnabledIndex = (startIndex: number) => {
      for (let i = startIndex + 1; i < menuItemsCount; i++) {
        if (disabledIndexes.indexOf(i) < 0) return i
      }
      return startIndex
    }
    const prevEnabledIndex = (startIndex: number) => {
      for (let i = startIndex - 1; i >= 0; i--) {
        if (disabledIndexes.indexOf(i) < 0) return i
      }
      return startIndex
    }
    if (e.key === 'ArrowDown') {
      if (selectedIndex === menuItemsCount - 1) return
      dispatch({ type: 'selectIndex', index: nextEnabledIndex(selectedIndex) })
    } else if (e.key === 'ArrowUp') {
      if (selectedIndex === 0) return
      dispatch({ type: 'selectIndex', index: prevEnabledIndex(selectedIndex) })
    } else if (e.key === 'Home') {
      const firstEnabledIndex = nextEnabledIndex(-1)
      if (selectedIndex === firstEnabledIndex) return
      dispatch({ type: 'selectIndex', index: firstEnabledIndex })
    } else if (e.key === 'End') {
      const lastEnabledIndex = prevEnabledIndex(menuItemsCount)
      if (selectedIndex === lastEnabledIndex) return
      dispatch({ type: 'selectIndex', index: lastEnabledIndex })
    } else if (e.key === 'Escape' || e.key === 'Tab') {
      dispatch({ type: 'close', restoreFocus: true })
      e.stopPropagation()
    }
  }

  return (
    <Popover ref={paperRef} referenceNode={buttonRef!.current} open={open} placement='bottom-start'>
      <List ref={listRef} id={menuId} role='menu' onKeyDown={handleKeyDown} {...rest}>
        {renderChildren()}
      </List>
    </Popover>
  )
}

export default MenuList
